import { SWRFetcher } from '@commenda/utils';
import { useEffect, useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import { CommendaService, CommendaServicesResponse } from './types';

const PAGE_SIZE = 100;

function buildKey(limit: number, offset: number) {
  const qs = new URLSearchParams();
  qs.set('limit', String(limit));
  qs.set('offset', String(offset));
  return `commenda-services?${qs}`;
}

export function useAllCommendaServices(enabled = true) {
  const getKey = (
    pageIndex: number,
    prevPage: CommendaServicesResponse | null
  ): string | null => {
    if (!enabled) return null;
    if (prevPage && pageIndex * PAGE_SIZE >= prevPage.total) return null;
    return buildKey(PAGE_SIZE, pageIndex * PAGE_SIZE);
  };

  const { data, size, setSize, isValidating, mutate } =
    useSWRInfinite<CommendaServicesResponse>(getKey, SWRFetcher({ method: 'GET' }), {
      revalidateFirstPage: false,
    });

  const items = useMemo<CommendaService[]>(
    () => (data ? data.flatMap((page) => page?.items ?? []) : []),
    [data]
  );
  const total = data?.[0]?.total ?? 0;
  const hasMore = !!data && items.length < total;

  useEffect(() => {
    if (hasMore && !isValidating) setSize((current) => current + 1);
  }, [hasMore, isValidating, setSize]);

  return { items, isLoading: !data || hasMore, mutate };
}
